import {
  AlertBar as RemoteAlertBar,
  AlertBarColors,
  styleConstants,
} from '@paywithextend/components'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { createSetIsDowngradeBarVisible } from 'src/app/AlertBar/redux/alertBar.actions'
import OrgRoleAcknowledgementsPopup from 'src/components/OrgRoleAcknowledgementsPopup/OrgRoleAcknowledgementsPopup'
import SystemAlertPopup from 'src/components/SystemAlertPopup/SystemAlertPopup'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { ScreeningStatus } from 'src/models/virtualCard.models'
import Lnk from 'src/navigation/Lnk'
import { PRICING_ROUTE, userScreeningInfoUrl } from 'src/navigation/urls'
import { useQueryOrganizationAcknowledgements } from 'src/queries/organizations/acknowledgements.queries'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import {
  useQueryOrganizationSaasTier,
  useQueryOrganizationUsages,
} from 'src/queries/organizations/saas.queries'
import { createSetIsAcknowledgementAlertBarOpen } from 'src/redux/actions/app.actions'
import {
  selectAlertMessageFromState,
  selectIsAlertBarOpenFromState,
  selectIsDowngradeBarOpenFromState,
  selectMessageCountFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import {
  selectUserHasFailScreening,
  selectUserHasForbiddenScreening,
} from 'src/redux/selectors/auth.selectors'
import {
  selectAcknowledgementPushdown,
  selectAlertBarPushdown,
  selectDowngradePushdown,
  selectFailPushdown,
  selectForbiddenPushdown,
} from 'src/redux/selectors/css.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import {
  selectCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsPendingSourceUserMeCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import { css } from 'styled-components'

import SvgArrowRightLoD from '../Svgs/SvgArrowRightLoD'

const AlertBars: React.FC = (): React.ReactElement => {
  const translate = useTranslate()

  const isAlertBarOpen = useSelector(selectIsAlertBarOpenFromState)

  const user = useCurrentUser()
  const org = useSelector(selectUserExplicitOrganizationListModelFromState)
  const orgId = org?.id

  const { data: orgFeatureFlags, status: orgFFStatus } =
    useQueryOrganizationFeatureFlags({ orgId, enabled: orgId !== undefined })
  const { data: orgAcknowledgements, status: orgAcknowledgementsStatus } =
    useQueryOrganizationAcknowledgements({
      enabled:
        orgFeatureFlags !== undefined &&
        orgFeatureFlags?.rolesMigrationAcksEnabled,
    })

  const userActiveAccounts = useSelector(
    selectCreditCardsActiveSourceCanIssueVirtualCardsCountStatisticsFromState,
  )

  const userPendingAccounts = useSelector(
    selectCreditCardsPendingSourceUserMeCountStatisticsFromState,
  )
  const userActiveAndPendingAccounts = userActiveAccounts + userPendingAccounts

  const [showSystemAlertPopup, setShowSystemAlertPopup] =
    useState<boolean>(false)
  const [showAcknowledgeRoleChangesPopup, setShowAcknowledgeRoleChangesPopup] =
    useState<boolean>(true)

  const dispatch = useBackgroundDispatch()

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.CreditCardsUserOrViewerMeCountStatistics,
      appStatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCountStatistics,
    ])
  }, [dispatchStatistics])

  const { data: orgUsages } = useQueryOrganizationUsages({
    orgId: orgId,
  })

  const { data: currentPlan } = useQueryOrganizationSaasTier({
    orgId: orgId,
    enabled: !!orgId,
  })

  useEffect(() => {
    if (user === undefined || orgUsages === undefined) {
      return
    }
    const showDowngradeMessage =
      user.hasOrgAdminPermissions &&
      currentPlan?.tier === 'STARTER' &&
      (!orgUsages.features.ORGANIZATION_TOTAL_SIZE?.withinLimit ||
        !orgUsages.features.GUEST_ROLE?.withinLimit)
    dispatch(createSetIsDowngradeBarVisible(showDowngradeMessage))
  }, [user, orgUsages, currentPlan])

  const alertMessage = useSelector(selectAlertMessageFromState)
  const messageCount = useSelector(selectMessageCountFromState)

  const userHasForbiddenScreening = useSelector(selectUserHasForbiddenScreening)
  const userHasFailScreening = useSelector(selectUserHasFailScreening)
  const [
    userHasNotAcknowledgedRoleUpdates,
    setUserHasNotAcknowledgedRoleUpdates,
  ] = useState<boolean>(false)

  const alertBarPushdown = useSelector(selectAlertBarPushdown)
  const forbiddenPushdown = useSelector(selectForbiddenPushdown)
  const failPushdown = useSelector(selectFailPushdown)
  const downgradePushdown = useSelector(selectDowngradePushdown)
  const acknowledgementPushDown = useSelector(selectAcknowledgementPushdown)
  const isDowngradeBarVisible = useSelector(selectIsDowngradeBarOpenFromState)

  useEffect(() => {
    const showAlertBanner = orgFeatureFlags?.rolesMigrationAcksEnabled
      ? !orgAcknowledgements?.acknowledgement.acknowledged &&
        userActiveAndPendingAccounts > 0
      : false
    if (orgAcknowledgementsStatus === 'success' && orgFFStatus === 'success') {
      dispatch(createSetIsAcknowledgementAlertBarOpen(showAlertBanner))
      setUserHasNotAcknowledgedRoleUpdates(showAlertBanner)
    }
  }, [
    orgAcknowledgementsStatus,
    orgAcknowledgements,
    orgFFStatus,
    userActiveAndPendingAccounts,
  ])

  useEffect(() => {
    const showAlertBanner = orgFeatureFlags?.rolesMigrationAcksEnabled
      ? !orgAcknowledgements?.acknowledgement.acknowledged &&
        userActiveAndPendingAccounts > 0
      : false
    if (orgAcknowledgementsStatus === 'success') {
      dispatch(createSetIsAcknowledgementAlertBarOpen(showAlertBanner))
      setUserHasNotAcknowledgedRoleUpdates(showAlertBanner)
    }
  }, [orgAcknowledgements])

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        width: calc(100%);
        max-width: calc(100%);
      `}
    >
      {isAlertBarOpen && (
        <>
          <RemoteAlertBar
            alertMessage={alertMessage}
            messageCount={messageCount}
            messageTitle={translate('System alert')}
            link={
              <span
                onClick={(): void => setShowSystemAlertPopup(true)}
                onKeyPress={(): void => setShowSystemAlertPopup(true)}
                role="button"
                tabIndex={0}
                aria-label={'view-status-button'}
                css={css`
                  cursor: pointer;
                `}
              >
                {translate('View status')}
              </span>
            }
            barColor={AlertBarColors.cream}
            css={css`
              z-index: 7;
              top: ${alertBarPushdown}px;
            `}
          />
          {showSystemAlertPopup && (
            <SystemAlertPopup
              onClose={(): void => setShowSystemAlertPopup(false)}
            />
          )}
        </>
      )}
      {userHasForbiddenScreening && (
        <RemoteAlertBar
          alertMessage={translate(
            'You have been locked for security concerns.',
          )}
          messageTitle={translate('Alert')}
          link={
            <Lnk
              ariaLabelSource={'Learn more'}
              to={{
                pathname: userScreeningInfoUrl(),
                search: `?status=${ScreeningStatus.Forbidden}`,
                state: {},
                hash: '',
              }}
            >
              {translate('Learn more')}
            </Lnk>
          }
          barColor={AlertBarColors.red}
          css={css`
            z-index: 7;
            top: ${forbiddenPushdown}px;
          `}
        />
      )}
      {userHasFailScreening && (
        <RemoteAlertBar
          alertMessage={translate(
            'You have been flagged for security concerns.',
          )}
          messageTitle={translate('Alert')}
          link={
            <Lnk
              ariaLabelSource={'Learn more'}
              to={{
                pathname: userScreeningInfoUrl(),
                search: `?status=${ScreeningStatus.Fail}`,
                state: {},
                hash: '',
              }}
            >
              {translate('Learn more')}
            </Lnk>
          }
          barColor={AlertBarColors.yellow}
          css={css`
            z-index: 7;
            top: ${failPushdown}px;
          `}
        />
      )}
      {isDowngradeBarVisible && (
        <RemoteAlertBar
          alertMessage={translate(
            'Your organization is on the Starter plan, which includes up to 5 active users and 10 guests. To continue using Extend, please deactivate excess users or upgrade.',
          )}
          messageTitle={translate('Alert')}
          link={
            <a
              href={PRICING_ROUTE()}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={'pricing-link'}
              css={css`
                cursor: pointer;
                display: flex;
                align-items: center;
                gap: 2px;
              `}
            >
              {translate('Subscribe now')}
              <SvgArrowRightLoD
                height={14}
                width={14}
                fillColor={styleConstants.colors.font_gray_dark}
              />
            </a>
          }
          barColor={AlertBarColors.yellow}
          css={css`
            z-index: 7;
            top: ${downgradePushdown}px;
          `}
        />
      )}
      {userHasNotAcknowledgedRoleUpdates && (
        <>
          <RemoteAlertBar
            alertMessage={translate(
              'Please review and acknowledge role permission changes to continue using the most up-to-date version of Extend.',
            )}
            messageTitle={translate('ACTION REQUIRED')}
            link={
              <span
                onClick={(): void => setShowAcknowledgeRoleChangesPopup(true)}
                onKeyPress={(): void =>
                  setShowAcknowledgeRoleChangesPopup(true)
                }
                role="button"
                tabIndex={0}
                aria-label={'click to acknowledge changes'}
                css={css`
                  cursor: pointer;
                `}
              >
                {translate('Click here to acknowledge changes')}
              </span>
            }
            barColor={AlertBarColors.yellow}
            css={css`
              z-index: 7;
              top: ${acknowledgementPushDown}px;
            `}
          />
          {showAcknowledgeRoleChangesPopup && (
            <OrgRoleAcknowledgementsPopup
              onClose={(): void => setShowAcknowledgeRoleChangesPopup(false)}
            />
          )}
        </>
      )}
    </div>
  )
}

export default AlertBars
